import { $, _ } from "../../vendor";

/**
 * _$ component
 *
 * Enables JQuery-like syntax for accessing and manipulating
 * the DOM. Supports IE >= 11, though several fallbacks
 * were provided
 *
 * Selections (this.elems) are being saved as array, not NodeList
 *
 * @author Felix Albroscheit
 * @thanks https://github.com/nefe/You-Dont-Need-jQuery#readme
 * @todo tests
 * @todo enable typescript
 *
 */

export default (function () {
  "use strict";

  const NodeListToArray: (_nodeList: any) => any[] = function (_nodeList: any): any[] {
    return Array.prototype.slice.call(_nodeList);
  };

  /**
   * Create the constructor
   * @param {String} selector The selector to use
   */
  const Constructor = function (this: any, selector: any): void {
    if (!selector) return;

    this.elems = [];

    if (typeof selector === "object") {
      this.elems.push(selector);
    } else if (selector === "document") {
      this.elems.push([document]);
    } else if (selector === "window") {
      this.elems.push([window]);
    } else {
      this.elems = NodeListToArray(document.querySelectorAll(selector));
    }
  };

  /**
   * Run a callback on each item
   * @param  {Function} callback The callback function to run
   */
  Constructor.prototype.each = function (callback: any): any {
    if (!callback || typeof callback !== "function") return;
    for (let i = 0; i < this.elems.length; i++) {
      callback(this.elems[i], i);
    }
    return this;
  };

  /**
   * Filter for children
   * @param {String} selector The selector to find children
   */
  Constructor.prototype.find = function (selector: any): any {
    const _elems: any[] = [];

    this.each(function (item: any): void {
      const a: any = item.querySelectorAll(selector);
      if (a !== null) {
        _.each(a, (_a: any): void => {
          _elems.push(_a);
        });
      }
    });

    this.elems = _elems;

    return this;
  };

  /**
   * Return the closest parent matching the selector
   * @param {String} selector
   */
  Constructor.prototype.closest = function (selector: any): any {
    // Temporarily fallback on $ to prevent
    // window.document === undefined error in IE :(
    const _elems: any[] = [];

    this.each(function (item: any): void {
      // @ts-ignore
      const a: JQuery<HTMLElementTagNameMap[keyof HTMLElementTagNameMap]> | undefined =
        $(item).closest(selector);

      if (typeof a !== "undefined" && a !== null) {
        _elems.push(a);
      }
    });

    this.elems = _elems;

    return this;

    // if (window.Element && !Element.prototype.closest) {
    //     let matches = (window.document || window.ownerDocument).querySelectorAll(selector),
    //         i,
    //         el = this;
    //     do {
    //         i = matches.length;
    //         while (--i >= 0 && matches.item(i) !== el) {};
    //     } while ((i < 0) && (el = el.parentElement));
    //
    //     this.elems = el;
    //
    // } else {
    //   const _elems = [];
    //   this.each(function(item) {
    // 	const a = item.closest(selector);
    // 	if (a !== null) {
    // 		_elems.push(a)
    // 	}
    //   });
    //   this.elems = _elems;
    // }
    // return this;
  };

  /**
   * Get or set elements attribute
   *
   * @param {String} key If no val provided, this will return a string or array
   * @param {String} val Optional, attribute-value to be set
   *
   * @todo If multiple elems in scope, return array
   */
  Constructor.prototype.attr = function (key: string, val: any = null): any {
    if (val === null) {
      if (typeof this.elems[0] !== "undefined") {
        return this.elems[0].getAttribute(key);
      }
    } else {
      this.each(function (item: any): void {
        item.setAttribute(key, val);
      });
    }
  };

  Constructor.prototype.parent = function (tagname: string, el: any): any {
    return this;
    // @todo Uncomplete
    // this.each(function(item) {
    //   while (item){
    // 	if ((el.nodeName || el.tagName).toLowerCase()===tagname.toLowerCase()){
    // 	  return el;
    // 	}
    // 	el = el.parentNode;
    //   }
    //   return null;
    // });
    // return this;
  };

  /**
   * Get the first element in scope
   */
  Constructor.prototype.first = function (): any {
    if (this.elems && typeof this.elems[0] !== "undefined") {
      this.elems = this.elems[0];
    }
    return this;
  };

  Constructor.prototype.hide = function (): any {
    this.each(function (item: any): void {
      item.style.display = "none";
    });
    return this;
  };

  Constructor.prototype.show = function (): any {
    this.each(function (item: any): void {
      item.style.display = "block";
    });
    return this;
  };

  /**
   * Remove selection from DOM
   * NOT chainable
   * @return 'undefined'
   */
  Constructor.prototype.remove = function (): void {
    this.each(function (item: any): void {
      item.parentNode.removeChild(item);
    });
  };

  /**
   * Add a class to elements
   * @param {String} className The class name
   */
  Constructor.prototype.addClass = function (className: any): any {
    this.each(function (item: any): void {
      item.classList.add(className);
    });
    return this;
  };

  /**
   * Remove a class to elements
   * @param {String} className The class name
   */
  Constructor.prototype.removeClass = function (className: any): any {
    this.each(function (item: any): void {
      item.classList.remove(className);
    });
    return this;
  };

  /**
   * Instantiate a new constructor
   */
  const instantiate = function (selector: any) {
    // @ts-ignore
    return new Constructor(selector);
  };

  /**
   * Return the constructor instantiation
   */
  return instantiate;
})();
